import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { ViewSwitcher } from "@/components/ViewSwitcher";
import { Pagination } from "@/components/Pagination";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Mail, Phone, MapPin, Users, Loader2, GraduationCap, Search } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", phone: "", address: "" };
const PAGE_SIZE = 20;

function Avatar({ name, size = "h-14 w-14", rounded = "rounded-2xl", text = "text-lg" }) {
  return (
    <div className={`${size} flex flex-shrink-0 items-center justify-center ${rounded} bg-gradient-to-br from-blue-500 to-violet-600 ${text} font-bold text-white`}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

export default function Parents() {
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("card");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/users?role=parent"), api.get("/students")]);
      setParents(p.data); setStudents(s.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const studentsOf = (parentId) => students.filter((s) => s.parent_id === parentId);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parents;
    return parents.filter((p) =>
      p.name.toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q) ||
      (p.phone || "").toLowerCase().includes(q)
    );
  }, [parents, search]);

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setSelectedStudents([]); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name || "", email: p.email || "", password: "", phone: p.phone || "", address: p.address || "" });
    setSelectedStudents(studentsOf(p.id).map((s) => s.id));
    setOpen(true);
  };
  const toggleStudent = (id) => setSelectedStudents((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const upd = { name: form.name, phone: form.phone, address: form.address, email: form.email };
        if (form.password) upd.password = form.password;
        await api.put(`/users/${editing.id}`, upd);
        await api.put(`/users/${editing.id}/students`, { student_ids: selectedStudents });
        toast.success("Veli güncellendi");
      } else {
        await api.post("/users", {
          email: form.email, password: form.password, name: form.name,
          role: "parent", phone: form.phone, address: form.address, student_ids: selectedStudents,
        });
        toast.success("Veli eklendi");
      }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/users/${deleteTarget.id}`); toast.success("Veli silindi"); setDeleteTarget(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="parents-page">
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Yönetim</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Veliler</h1>
            <p className="mt-2 text-sm text-slate-600">Veli hesabı oluştur ve öğrencilerle eşleştir.</p>
          </div>
          <Button onClick={openCreate} data-testid="add-parent-button" className="h-11 rounded-xl bg-violet-600 font-semibold text-white hover:bg-violet-700 active:scale-[0.98]">
            <Plus className="mr-1.5 h-4 w-4" /> Veli Ekle
          </Button>
        </header>

        <div className="mb-6 flex flex-wrap items-center gap-3">
          <div className="relative min-w-[220px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="İsim, e-posta veya telefona göre ara..." className="h-11 rounded-xl pl-10" data-testid="parents-search" />
          </div>
          <ViewSwitcher value={view} onChange={setView} />
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="parents-empty">
            <Users className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            {parents.length === 0 ? "Henüz veli yok. “Veli Ekle” ile başla." : "Aramanızla eşleşen veli yok."}
          </Card>
        ) : view === "card" ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            {paged.map((p) => {
              const kids = studentsOf(p.id);
              return (
                <Card key={p.id} data-testid={`parent-card-${p.id}`}
                  className="overflow-hidden rounded-2xl border-slate-200/60 p-0 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.03)] transition-transform hover:-translate-y-1">
                  <div className="flex items-center gap-4 border-t-4 border-blue-500 p-5">
                    <Avatar name={p.name} />
                    <div className="min-w-0">
                      <h3 className="truncate font-heading text-lg font-semibold text-slate-900">{p.name}</h3>
                      <p className="flex items-center gap-1 truncate text-sm text-slate-500"><Mail className="h-3.5 w-3.5" />{p.email}</p>
                    </div>
                  </div>
                  <div className="space-y-1.5 border-t border-slate-100 px-5 py-4 text-sm text-slate-600">
                    <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-blue-500" />{p.phone || "Telefon yok"}</p>
                    <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-violet-500" />{p.address || "Adres yok"}</p>
                    <div className="flex flex-wrap items-center gap-1.5 pt-1">
                      <GraduationCap className="h-4 w-4 text-emerald-500" />
                      {kids.length === 0 ? <span className="text-slate-400">Öğrenci atanmadı</span> :
                        kids.map((k) => <Badge key={k.id} className="rounded-full bg-violet-100 text-violet-700 hover:bg-violet-100">{k.full_name}</Badge>)}
                    </div>
                  </div>
                  <div className="flex gap-2 border-t border-slate-100 px-5 py-3">
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)} data-testid={`edit-parent-${p.id}`} className="rounded-lg"><Pencil className="mr-1 h-3.5 w-3.5" /> Düzenle</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)} data-testid={`delete-parent-${p.id}`} className="rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="mr-1 h-3.5 w-3.5" /> Sil</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="overflow-x-auto rounded-2xl border-slate-200/60 shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-testid="parents-list">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                  <th className="px-5 py-3">Veli</th>
                  <th className="px-5 py-3">Telefon</th>
                  <th className="px-5 py-3">Adres</th>
                  <th className="px-5 py-3">Öğrenciler</th>
                  <th className="px-5 py-3 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((p) => {
                  const kids = studentsOf(p.id);
                  return (
                    <tr key={p.id} data-testid={`parent-row-${p.id}`} className="border-b border-slate-50 transition-colors last:border-0 hover:bg-slate-50/60">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={p.name} size="h-9 w-9" rounded="rounded-lg" text="text-sm" />
                          <div><p className="font-medium text-slate-800">{p.name}</p><p className="text-xs text-slate-400">{p.email}</p></div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{p.phone || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{p.address || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{kids.length > 0 ? kids.map((k) => k.full_name).join(", ") : <span className="text-slate-400">—</span>}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-parent-${p.id}`} className="h-8 w-8 rounded-lg"><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(p)} data-testid={`delete-parent-${p.id}`} className="h-8 w-8 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        {!loading && <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="parent-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? "Veli Düzenle" : "Yeni Veli"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 py-2">
            <div className="space-y-2"><Label>Ad Soyad *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="parent-name-input" /></div>
            <div className="space-y-2"><Label>E-posta *</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="parent-email-input" className="rounded-xl" /></div>
            <div className="space-y-2"><Label>{editing ? "Yeni Şifre (opsiyonel)" : "Şifre *"}</Label><Input type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Değiştirmek için doldur" : ""} data-testid="parent-password-input" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="parent-phone-input" /></div>
              <div className="space-y-2"><Label>Adres</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="parent-address-input" /></div>
            </div>
            <div className="space-y-2">
              <Label>Öğrenci Ata</Label>
              <div className="max-h-44 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2">
                {students.length === 0 ? <p className="p-2 text-sm text-slate-400">Önce öğrenci ekleyin.</p> :
                  students.map((s) => {
                    const takenByOther = s.parent_id && s.parent_id !== editing?.id;
                    return (
                      <label key={s.id} data-testid={`assign-student-${s.id}`} className="flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50">
                        <Checkbox checked={selectedStudents.includes(s.id)} onCheckedChange={() => toggleStudent(s.id)} />
                        <span className="text-sm text-slate-700">{s.full_name}</span>
                        {takenByOther && <span className="ml-auto text-xs text-amber-600">({s.parent_name})</span>}
                      </label>
                    );
                  })}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">İptal</Button>
              <Button type="submit" disabled={saving} data-testid="parent-save-button" className="rounded-xl bg-violet-600 text-white hover:bg-violet-700">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-parent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Veliyi sil?</AlertDialogTitle>
            <AlertDialogDescription><strong>{deleteTarget?.name}</strong> hesabı silinecek ve öğrenci bağlantıları kaldırılacak.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-parent" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
